import { humanize } from './paramUnits';
// ONE ROW PER PROPERTY, EVEN WHEN THE PROPERTY IS NOT A STRING.
//
// The properties panel has always rendered a record by walking its own
// entries and writing key-colon-value into a template literal. That is
// fine for every field the exporter had ever written, because they were
// all scalars. A template literal meeting an OBJECT writes the literal
// text "[object Object]", and meeting a long array writes every element
// joined by commas.
//
// Tom's 2026-09-24 export (ea4f2cd) adds the first nested object a record
// has ever carried - Assembly, describing a nested family's parts - so
// the panel was one publish away from showing "Assembly: [object Object]"
// to the first person who clicked a hanger. This module is the general
// answer rather than a special case for that one key: the next field the
// exporter nests will already be handled.
//
// WHAT DELIBERATELY DID NOT CHANGE: a SCALAR still renders exactly as it
// did, through the same `${value}` conversion. Every field that exists
// today is a scalar, so this change cannot alter a single row anyone is
// looking at now - including the ones that read "null" or "undefined",
// which are left alone on purpose. The blast radius is exactly the
// values that used to be unreadable.
//
// WHY ARRAYS OF OBJECTS ARE COUNTED, NOT EXPANDED: Assembly.Groups names
// every part id, and across SpaceX that is ~1.2M ids. A properties panel
// that pastes them is not a panel. A count is the useful fact; the
// machine-readable list is in the record for code, not for a person.

const INLINE_ARRAY_MAX = 8;   // scalars beyond this are counted, not listed
const MAX_ROWS_NESTED = 12;   // per nested object; the TOP level is never capped
const MAX_DEPTH = 2;

// Not `typeof v === 'object'`: a Date is an object with no own entries, so
// it would render as a header with nothing under it instead of its value.
function isPlainObject(v) {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

function isScalar(v) {
  return !Array.isArray(v) && !isPlainObject(v);
}

function arrayText(arr) {
  if (arr.length === 0) return '(empty)';
  if (arr.length <= INLINE_ARRAY_MAX && arr.every(isScalar)) return arr.join(', ');
  return `${arr.length} items`;
}

function fieldCountText(obj) {
  const n = Object.keys(obj).length;
  return `${n} ${n === 1 ? 'field' : 'fields'}`;
}

// A measure and its unit are two keys that mean one thing: Length and
// LengthUnit. Pairing them by NAME rather than by a list of known units
// means Tom can add PressureUnit without touching this file.
function unitKeysOf(obj) {
  const consumed = new Set();
  for (const k of Object.keys(obj)) {
    if (!k.endsWith('Unit') || k.length <= 4) continue;
    const base = k.slice(0, -4);
    if (Object.prototype.hasOwnProperty.call(obj, base)
        && isScalar(obj[base]) && typeof obj[k] === 'string' && obj[k]) {
      consumed.add(k);
    }
  }
  return consumed;
}

/**
 * Flatten a record into display rows.
 * Each row: { id, depth, label, text }. `text` empty means a header row
 * (an object's name, with its fields following at depth + 1).
 */
export function recordFieldRows(data, options) {
  const opts = options || {};
  const omit = new Set(opts.omit || []);
  const depth = opts.depth || 0;
  const path = opts.path || '';
  const rows = [];
  if (!isPlainObject(data)) return rows;

  const units = unitKeysOf(data);
  // The top level is a record the owner reads in full. Only NESTED
  // objects are capped - capping the record itself would hide fields
  // that render correctly today.
  const cap = depth === 0 ? Infinity : MAX_ROWS_NESTED;
  let shown = 0;
  let hidden = 0;

  for (const [key, value] of Object.entries(data)) {
    if (omit.has(key) || units.has(key)) continue;
    if (shown >= cap) { hidden += 1; continue; }
    shown += 1;
    const id = `${path}${key}`;

    if (Array.isArray(value)) {
      rows.push({ id, depth, label: key, text: arrayText(value) });
      continue;
    }
    if (isPlainObject(value)) {
      if (depth + 1 > MAX_DEPTH) {
        rows.push({ id, depth, label: key, text: fieldCountText(value) });
        continue;
      }
      rows.push({ id, depth, label: key, text: '' });
      for (const r of recordFieldRows(value, { depth: depth + 1, path: `${id}.` })) rows.push(r);
      continue;
    }
    const unit = units.has(`${key}Unit`) ? data[`${key}Unit`] : null;
    // A parameter exported in Revit internal units with no unit beside it
    // reads as its real value (helpers/paramUnits): 208 V, not 2238.89.
    const human = unit ? null : humanize(key, value);
    rows.push({ id, depth, label: key, text: unit ? `${value} ${unit}` : (human ? human.text : `${value}`), raw: human ? value : undefined });
  }

  if (hidden > 0) {
    rows.push({ id: `${path}__more`, depth, label: `+ ${hidden} more`, text: '' });
  }
  return rows;
}

export const __test = { INLINE_ARRAY_MAX, MAX_ROWS_NESTED, MAX_DEPTH };
